import { useState, useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  SidePanel,
  SidePanelType,
  PermissionDenied,
  PermissionEnum,
  ButtonPseudo,
  ButtonPrimary,
  Popup,
  ResponseError,
  responseErrorsResolver,
  InputNumber,
  Select,
  SidePanelLoader,
  ChangeLogs,
  Tabs,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { clearPanelState } from '../store/slices/types-risk';
import {
  useCreateTransactionTypesRiskMutation,
  useLazyGetTransactionTypesRiskQuery,
  useLazyGetTransactionTypeRiskQuery,
  useUpdateTransactionTypesRiskMutation,
  useLazyGetTransactionTypesRiskChangeLogsQuery,
} from '../store/services';

import ROUTES from '../../../routes';

import cn from '../transactions-risk.module.css';

const CreateTransactionsTypesRiskScore = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'logs'>('general');

  const {
    transactionsRisk: {
      typesRisk: { typeRisk, changeLogs, changeLogsPagination },
    },
    amlDictionaries: { activePermissions, transactionTypes },
    administrationDictionaries: { users },
    settings: {
      paymentTypes: { paymentTypes },
    },
  } = useAppSelector((state) => state);

  const [
    createTransactionTypesRisk,
    { isLoading: createInProgress, isSuccess },
  ] = useCreateTransactionTypesRiskMutation();
  const [getTransactionTypesRisk] = useLazyGetTransactionTypesRiskQuery();
  const [getTransactionTypeRisk, { isLoading, isFetching }] =
    useLazyGetTransactionTypeRiskQuery();
  const [updateTransactionTypeRisk, { isLoading: requestInProgress }] =
    useUpdateTransactionTypesRiskMutation();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetTransactionTypesRiskChangeLogsQuery();

  const dispatch = useAppDispatch();

  const { search } = useLocation();
  const navigate = useNavigate();

  const { typeId } = useParams();

  const {
    control,
    formState: { isDirty, errors },
    handleSubmit,
    setError,
    reset,
    watch,
  } = useForm<{
    paymentType?: string;
    riskScore: number;
    transactionTypeId: string;
  }>();

  const transactionTypeId = watch('transactionTypeId');

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=PAYMENT_ON_TRANSACTION_TYPE&entityId=${typeId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [typeId],
  );

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    if (
      !Number.isInteger(Number(values.riskScore)) ||
      Number.isNaN(Number(values.riskScore))
    ) {
      setError('riskScore', {
        type: 'custom',
        message: 'Risk Score must be an integer number',
      });
    } else {
      const body: {
        paymentType?: string;
        riskScore?: number;
        transactionTypeId: string;
      } = {
        transactionTypeId: values.transactionTypeId,
        riskScore: Number(values.riskScore),
      };

      if (values.paymentType) {
        body.paymentType = values.paymentType;
      }

      let response;

      if (!typeId) {
        response = (await createTransactionTypesRisk(
          body as {
            paymentType?: string;
            riskScore: number;
            transactionTypeId: string;
          },
        )) as {
          error?: ResponseError;
        };
      } else {
        response = (await updateTransactionTypeRisk({
          id: typeId,
          body: body as {
            paymentType?: string;
            riskScore: number;
            transactionTypeId: string;
          },
        })) as { error?: ResponseError };
      }

      if (response?.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError,
        });
      } else if (!response.error && typeId) {
        reset(body);
      }
    }
  });

  const handleClose = () => {
    setPanelIsOpen(false);
    setGlobalError('');
    dispatch(clearPanelState());
    reset({});

    setTimeout(() => {
      navigate(`${ROUTES.TRANSACTIONS_RISK_TYPES_RISK}${search || ''}`);
    }, 100);
  };

  useEffect(() => {
    if (typeRisk) {
      reset({
        paymentType: typeRisk.paymentType || '',
        riskScore: typeRisk.riskScore,
        transactionTypeId: typeRisk.transactionTypeId,
      });
    }
  }, [typeRisk]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (
      typeId &&
      activePermissions?.includes(
        PermissionEnum.SETTINGS_TRANSACTIONS_TYPES_RISK_SCORE_LIST,
      )
    ) {
      getTransactionTypeRisk(typeId);
    }
  }, [typeId]);

  useEffect(() => {
    if (
      activeTab === 'logs' &&
      !changeLogs &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [activeTab]);

  useEffect(() => {
    if (isSuccess) {
      getTransactionTypesRisk(
        `?take=30${search ? search.replace('?', '&') : ''}`,
      );
      handleClose();
    }
  }, [isSuccess]);

  let content;

  if (
    !activePermissions?.includes(
      PermissionEnum.SETTINGS_TRANSACTIONS_TYPES_RISK_SCORE_CREATE_ACTION,
    ) &&
    !typeId
  ) {
    content = <PermissionDenied />;
  } else {
    // eslint-disable-next-line no-lonely-if
    if (isLoading || isFetching) {
      content = <SidePanelLoader />;
    } else {
      content = (
        <div className={cn.transactionTypesForm}>
          {typeId && (
            <Tabs
              tabs={[
                {
                  name: 'Details',
                  value: 'general',
                },
                {
                  name: 'Change log',
                  value: 'logs',
                },
              ]}
              wrapperClassName={cn.transactionTypesTabs}
              activeTab={activeTab}
              onClick={(tab) => setActiveTab(tab as 'general' | 'logs')}
            />
          )}
          {activeTab === 'general' && (
            <>
              <div className={cn.formGroup}>
                <Controller
                  control={control}
                  name="transactionTypeId"
                  render={({ field: { onChange, value, name } }) => (
                    <Select
                      name={name}
                      value={value}
                      list={
                        transactionTypes
                          ?.filter(
                            (item) =>
                              item.defaulName === 'INCOMING' ||
                              item.defaulName === 'OUTGOING' ||
                              item.defaulName === 'INTERNAL',
                          )
                          .map((item) => ({
                            name: item.name,
                            value: item?.id,
                          })) || []
                      }
                      onChange={onChange}
                      error={errors?.transactionTypeId?.message}
                      title="Transaction type"
                      placeholder="Select transaction type"
                      fullWidth
                    />
                  )}
                />
              </div>
              <div className={cn.formGroup}>
                <Controller
                  control={control}
                  name="riskScore"
                  render={({ field: { onChange, value, name } }) => (
                    <InputNumber
                      name={name}
                      value={value}
                      placeholder="Enter risk score"
                      title="Risk score"
                      onChange={(e) => {
                        const riskValue = e.target.value.replace(',', '.');

                        if (Number(riskValue) < 0) {
                          onChange(0);
                        } else if (Number(riskValue) > 100) {
                          onChange(100);
                        } else {
                          onChange(riskValue);
                        }
                      }}
                      error={errors?.riskScore?.message}
                    />
                  )}
                />
              </div>
              {(transactionTypes?.find((item) => item.defaulName === 'INCOMING')
                ?.id === transactionTypeId ||
                transactionTypes?.find((item) => item.defaulName === 'OUTGOING')
                  ?.id === transactionTypeId) && (
                <div className={cn.formGroup}>
                  <Controller
                    control={control}
                    name="paymentType"
                    render={({ field: { onChange, value, name } }) => (
                      <Select
                        name={name}
                        value={value}
                        list={
                          paymentTypes
                            ?.filter((item) => item.enabled)
                            ?.map((item) => ({
                              name: item.name,
                              value: item.paymentType,
                            })) || []
                        }
                        onChange={onChange}
                        error={errors?.paymentType?.message}
                        title="Payment type"
                        placeholder="Select payment type"
                        fullWidth
                      />
                    )}
                  />
                </div>
              )}
            </>
          )}
          {activeTab === 'logs' && (
            <>
              {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                <ChangeLogs
                  entityType="PAYMENT_ON_TRANSACTION_TYPE"
                  entityId={typeId as string}
                  changeLogsIsLoading={changeLogsIsLoading}
                  changeLogs={changeLogs}
                  activePermission={activePermissions}
                  users={users}
                  pagination={changeLogsPagination}
                  changeLogsIsFetching={changeLogsIsFetching}
                  getChangeLogs={getChangeLogsAction}
                />
              )}
              {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                <PermissionDenied />
              )}
            </>
          )}
        </div>
      );
    }
  }

  let buttonText = 'Add Risk Score';

  if (typeId) {
    buttonText = 'Save';
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.AML}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title={`${typeId ? 'Edit' : 'Add'} Risk Score`}
        errorSidepanel={globalError}
        sidepanelExpandedKey="types-score"
        footer={
          <>
            {(activePermissions?.includes(
              PermissionEnum.SETTINGS_TRANSACTIONS_TYPES_RISK_SCORE_CREATE_ACTION,
            ) ||
              (typeId &&
                activePermissions?.includes(
                  PermissionEnum.SETTINGS_TRANSACTIONS_TYPES_RISK_SCORE_UPDATE_ACTION,
                ))) && (
              <ButtonPrimary
                text={
                  createInProgress || requestInProgress
                    ? 'Loading...'
                    : buttonText
                }
                onClick={onSubmit}
                disabled={createInProgress || requestInProgress || !isDirty}
                bold
              />
            )}

            <ButtonPseudo
              text="Cancel"
              className="button-white"
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default CreateTransactionsTypesRiskScore;
